<template>
  <div class="protocol-gateway-detail">
    <a-card>
      <div class="detail-header">
        <div class="tabs">
          <a-tag color="#1677ff" style="font-size:16px;">协议详情</a-tag>
        </div>
        <div class="actions">
          <a-button @click="goBack" type="default">返回</a-button>
          <a-button type="default" @click="showEdit = true">编辑</a-button>
        </div>
      </div>
      <!-- 协议信息 -->
      <a-descriptions v-if="currentProtocol" bordered :column="4" size="middle" title="协议信息">
        <a-descriptions-item v-for="item in protocolInfo" :key="item.label" :label="item.label" :span="item.span">
          {{ item.value }}
        </a-descriptions-item>
      </a-descriptions>
      <a-empty v-else description="协议信息加载中或未找到..." />

      <!-- 服务状态 -->
      <div style="margin-top:24px;">
        <div style="font-weight:bold;margin-bottom:8px;">服务状态</div>
        <a-table :columns="columns" :data-source="serviceStatusData" :pagination="false" style="margin-top: 8px;">
          <template #status="{ text: status }">
            <a-tag :color="status === '运行中' ? 'green' : status === '已停止' ? 'red' : 'orange'">
              {{ status }}
            </a-tag>
          </template>
          <template #action="{ record }">
            <a-space>
              <a-button type="link" size="small" @click="handleServiceAction('重启', record)" :disabled="record.status !== '已停止' && record.status !== '异常'">重启</a-button>
              <a-button type="link" size="small" @click="handleServiceAction('停止', record)" :disabled="record.status !== '运行中'">停止</a-button>
              <a-button type="link" size="small" @click="handleServiceAction('启动', record)" :disabled="record.status === '运行中'">启动</a-button>
            </a-space>
          </template>
          <template #emptyText>
            <div style="text-align:center;padding:32px 0;">
              <a-empty description="暂无数据" />
            </div>
          </template>
        </a-table>
      </div>
    </a-card>
    <!-- 编辑弹窗 -->
    <a-modal v-model:open="showEdit" title="编辑" width="900px" :footer="null" :closable="true">
      <a-form layout="vertical">
        <a-form-item label="协议名称" required>
          <a-input value="联犀MQTT协议" readonly />
        </a-form-item>
        <a-form-item label="etcdKey">
          <a-input value="dg.rpc" readonly />
        </a-form-item>
        <a-form-item label="point" style="margin-bottom: 8px;">
          <div style="display: flex; align-items: center;">
            <a-input v-model:value="pointInput" placeholder="请输入point" style="width: 300px; margin-right: 8px;" />
            <a-button type="dashed" @click="handleAddPoint">新增</a-button>
          </div>
        </a-form-item>
        <template v-if="pointList.length">
          <div style="margin-bottom: 24px;">
            <a-tag
              v-for="(item, idx) in pointList"
              :key="item + idx"
              closable
              @close="pointList.splice(idx, 1)"
              style="margin-right: 8px;"
            >
              {{ item }}
            </a-tag>
          </div>
        </template>
        <a-form-item label="协议描述">
          <a-textarea placeholder="请输入协议描述" rows="3" />
        </a-form-item>
        <div style="text-align:right;margin-top:24px;">
          <a-button style="margin-right:8px;" @click="showEdit = false">取消</a-button>
          <a-button type="primary">确定</a-button>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import { protocolDataList, type ProtocolRecord } from '@/data/protocolData'
import { generateServiceStatus, type ServiceStatus } from '@/mock/protocolServiceMock'

const router = useRouter()
const route = useRoute()
const showEdit = ref(false)
const pointInput = ref('')
const pointList = ref<string[]>([])

const protocolCode = ref('')
const currentProtocol = ref<ProtocolRecord | undefined>(undefined)
const serviceStatusData = ref<ServiceStatus[]>([])

onMounted(() => {
  const code = route.params.code as string;
  if (code) {
    protocolCode.value = code;
    currentProtocol.value = protocolDataList.find(p => p.code === code);
    if (currentProtocol.value) {
      // 生成1到5条模拟服务状态数据
      const count = Math.floor(Math.random() * 5) + 1; 
      serviceStatusData.value = generateServiceStatus(count);
    } else {
      message.error('未找到对应的协议信息');
      // router.back(); // 可以选择返回上一页或显示错误信息
    }
  } else {
    message.error('协议Code未提供');
    // router.back();
  }
});

const protocolInfo = computed(() => {
  if (currentProtocol.value) {
    return [
      { label: '协议名称', value: currentProtocol.value.name },
      { label: '协议code', value: currentProtocol.value.code },
      { label: '协议类型', value: currentProtocol.value.type },
      { label: 'etcdKey', value: currentProtocol.value.etcdKey },
      { label: '协议服务端点', value: '-' }, // 假设服务端点暂时为'-'
      { label: '描述', value: currentProtocol.value.trans, span: 3 }, // 使用trans字段作为描述
    ];
  }
  return [];
});

function handleAddPoint() {
  if (!pointInput.value || !pointInput.value.trim()) {
    message.warning('Point不能为空')
    return
  }
  pointList.value.push(pointInput.value.trim())
  pointInput.value = ''
}
const columns = [
  { title: 'ID', dataIndex: 'id', key: 'id' },
  { title: 'IP地址', dataIndex: 'ip', key: 'ip' },
  { title: '端口', dataIndex: 'port', key: 'port' },
  {
    title: '服务状态',
    dataIndex: 'status',
    key: 'status',
    slots: { customRender: 'status' },
  },
  { title: '更新时间', dataIndex: 'updateTime', key: 'updateTime' },
  { title: '首次加入时间', dataIndex: 'joinTime', key: 'joinTime' },
  {
    title: '操作',
    key: 'action',
    slots: { customRender: 'action' },
  },
];

function goBack() {
  router.back()
}

const handleServiceAction = (action: string, record: ServiceStatus) => {
  message.info(`对服务 ${record.id} 执行 ${action} 操作`);
  // 这里可以添加实际的操作逻辑，例如调用API
  // 为了演示，我们简单地更新状态或模拟异步操作
  if (action === '重启') {
    record.status = '运行中';
  } else if (action === '停止') {
    record.status = '已停止';
  } else if (action === '启动') {
    record.status = '运行中';
  }
  // 强制刷新表格，如果状态更新不是响应式的
  serviceStatusData.value = [...serviceStatusData.value];
};

</script>

<style lang="less" scoped>
.protocol-gateway-detail {
  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    .tabs {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .actions {
      display: flex;
      gap: 8px;
    }
  }
}
</style>